import { useApolloClient } from '@apollo/client/react';
import { useState } from 'react';

import {
  BillingHistoryReceiptUrlDocument,
  type BillingHistoryReceiptUrlQuery,
  BillingPlanReceiptUrlDocument,
  type BillingPlanReceiptUrlQuery,
} from '@repo/frontend-shared';

/**
 * Shows a receipt without losing the billing page behind it.
 *
 * A new tab is opened only once there is something to put in it. Claiming one
 * up front would survive a stricter popup blocker, but it also opens a blank
 * tab on every click that turns out to have no receipt — a tab that says
 * something is coming when nothing is.
 *
 * `window.open` comes back null when the browser refused the tab, and that is
 * the only reason this navigates in place instead. `noopener` is set by hand
 * rather than as an option, because passing it makes the call return nothing
 * and there would be no way to tell a refusal from a success.
 */
function openReceipt(url: string): void {
  const receiptTab = window.open(url, '_blank');
  if (!receiptTab) {
    window.location.assign(url);
    return;
  }
  receiptTab.opener = null;
}

/**
 * Fetches a provider-hosted receipt at the moment someone asks for one.
 *
 * Nothing about a receipt is stored locally, so every one of these is a live
 * Stripe read. Requesting them per history row would make a page of twenty
 * cost twenty provider calls to serve the one that might be clicked, which is
 * why this is a query run on click rather than a field on the row.
 *
 * `onUnavailable` is what happens when the provider holds no receipt — the
 * billing portal, which is where the page sent people before receipts
 * resolved at all.
 */
export function useBillingReceipt({ onUnavailable }: { onUnavailable(): void }) {
  const client = useApolloClient();
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);

  const open = async (fetchUrl: () => Promise<string | undefined>): Promise<void> => {
    setIsReceiptLoading(true);
    try {
      const url = await fetchUrl();
      if (!url) {
        onUnavailable();
        return;
      }
      openReceipt(url);
    } catch {
      // A failed lookup is not a different outcome to a missing receipt: both
      // mean this page cannot show one, and the portal is what it offers then.
      onUnavailable();
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const openHistoryEntryReceipt = async (entryId: string): Promise<void> =>
    open(async () => {
      const result = await client.query<BillingHistoryReceiptUrlQuery>({
        query: BillingHistoryReceiptUrlDocument,
        variables: { entryId },
        // A receipt URL is a live provider read and is not worth replaying
        // from cache: a stale one leads to a page that no longer loads.
        fetchPolicy: 'network-only',
      });
      return result.data?.myBillingHistoryReceiptUrl ?? undefined;
    });

  const openPlanReceipt = async (): Promise<void> =>
    open(async () => {
      const result = await client.query<BillingPlanReceiptUrlQuery>({
        query: BillingPlanReceiptUrlDocument,
        fetchPolicy: 'network-only',
      });
      return result.data?.myBillingPlanReceiptUrl ?? undefined;
    });

  return { isReceiptLoading, openHistoryEntryReceipt, openPlanReceipt };
}
